import { useState, useEffect, useCallback } from 'react';
import { getDataWithQueryString, postData } from '../../../../../api/componentAPIs';
import { getExistingFeaturesGeoJSON } from '../utils';

const useDowLocationData = ({ terminalId, enqueueSnackbar, updateDowGeoDataList }) => {
  const [dowLocationMappingData, setDowLocationMappingData] = useState([]);
  const [isDowDataLoaded, setIsDowDataLoaded] = useState(false);
  const [isDowLocationDeleteInProgress, setIsDowLocationDeleteInProgress] = useState(false);
  const [filteredDowDeliveryData, setFilteredDowDeliveryData] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);

  useEffect(() => {
    applyFilter();
  }, [selectedDays, dowLocationMappingData]);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.message || 'Failed to load DOW mapping data', { variant: 'error' });
      setDowLocationMappingData([]);
      setFilteredDowDeliveryData([]);
      setDowGeoDataList([]);
      setIsDowDataLoaded(true);
    },
    [enqueueSnackbar]
  );

  const getDowLocationMappingData = useCallback(async () => {
    try {
      setIsDowDataLoaded(false);
      const response = await getDataWithQueryString(
        'GetDeliveryLocationMapping',
        `terminalId=${terminalId}`
      );

      if (response?.status >= 200 && response?.status < 400) {
        const formattedData =
          response.data?.map((item) => ({
            ...item,
            locationMappingId: item.deliveryLocationMappingId,
            locationName: item.deliveryLocationMappingName,
            dowDelWindow: item.deliveryDOWOpen,
            boundary: JSON.parse(item.boundary)
          })) || [];
        setDowLocationMappingData(formattedData);
        setIsDowDataLoaded(true);
      } else {
        handleError(new Error('Failed to fetch DOW mapping data'));
      }
    } catch (error) {
      handleError(error);
    }
  }, [terminalId, handleError, setDowGeoDataList]);

  const setDowGeoDataList = useCallback((formattedData) => {
    updateDowGeoDataList(getExistingFeaturesGeoJSON(formattedData));
  }, [updateDowGeoDataList]);

  const handleDeleteDowLocation = useCallback(
    async (location) => {
      setIsDowLocationDeleteInProgress(true);
      try {
        const response = await postData('DeleteDeliveryLocationMapping', {
          terminalId,
          deliveryLocationMappingId: location.locationMappingId
        });

        if (response?.status >= 200 && response?.status < 400) {
          enqueueSnackbar('DOW location deleted successfully', { variant: 'success' });
          await getDowLocationMappingData();
        } else {
          throw new Error('Failed to delete DOW location');
        }
      } catch (error) {
        enqueueSnackbar(error?.message || 'Failed to delete DOW location', { variant: 'error' });
      } finally {
        setIsDowLocationDeleteInProgress(false);
      }
    },
    [terminalId, enqueueSnackbar, getDowLocationMappingData]
  );

  const applyFilter = useCallback(() => {
    if (selectedDays?.length > 0) {
      const filteredData = dowLocationMappingData.filter((locationData) =>
        selectedDays.some((day) => locationData.deliveryDOWOpen?.includes(day) || (day === 'none' && !locationData.deliveryDOWOpen?.length))
      );
      setFilteredDowDeliveryData(filteredData);
      setDowGeoDataList(filteredData);
      return;
    }
    setFilteredDowDeliveryData(dowLocationMappingData);
    setDowGeoDataList(dowLocationMappingData);
  }, [selectedDays, dowLocationMappingData, setDowGeoDataList]);

  const handleDowFilterChange = useCallback((selectedDays) => {
    setSelectedDays(selectedDays);
  }, []);

  return {
    dowLocationMappingData,
    isDowDataLoaded,
    isDowLocationDeleteInProgress,
    getDowLocationMappingData,
    handleDeleteDowLocation,
    filteredDowDeliveryData,
    handleDowFilterChange
  };
};

export default useDowLocationData;
